/*  eslint-disable no-bitwise */
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { UtilityService } from './utility.service';
import { ApplicationStateService } from './application-state.service';

declare let AES_Init: any;
declare let AES_Encrypt: any;
declare let AES_Decrypt: any;
declare let AES_ExpandKey: any;

@Injectable({
    providedIn: 'root'
})
export class CryptService {

    chars: string;
    private primeKey = 'B3zwia5bO7y-6vGHu.0rZoMq';
    private ENCRYPT_DATA = true;
    private HSM_SESSION_TOKEN = 'HSM_SESSION';
    private HSM_SESSION_PREFIX = 'HSM_SESSION_BOOK_PREFIX';
    // private keyHash = this.utilityService.hash(this.primeKey);
    private TOKEN_KEY = 'TOKEN';

    constructor(private utilityService: UtilityService,
                private applicationStateService: ApplicationStateService,
                private cookieService: CookieService) {
        this.init();
    }

    private _key: Array<any>;

    get key(): Array<any> {
        if (!this._key) {
            this.setKey(this.primeKey);
        }

        return this._key;
    }

    /**
     * initiate aes encryption
     */
    init(): void {
        AES_Init();
        this.chars = this.utilityService.base64Chars;
    }

    /**
     * encrypt salted string
     *
     * @param {string} myString
     * @param {number} saltedLength
     * @return {string}
     */
    encryptSaltedString(myString: string, saltedLength?: number): string {
        if (!myString) {
            myString = '';
        }

        if (this.ENCRYPT_DATA) {
            let len = saltedLength;
            if (!len) {
                len = myString.length + 2;
            }

            let str = myString.substr(0, len - 2).replace('|', '');
            str += `|${this.utilityService.generateRandomString(len - 1 - str.length)}`;

            let data = [];
            for (let i = 0; i < len; i += 16) {
                data = data.concat(this.encrypt(str.substr(i, 16)));
            }

            return this.bin2url(data);
        }

        return myString;
    }

    /**
     * encrypt string
     *
     * @param inputStr
     * @return {Array<any>}
     */
    encrypt(inputStr): Array<any> {
        const block = this.string2Bin(inputStr);
        AES_Encrypt(block, this.key);

        return block;
    }

    /**
     * decrypt array
     *
     * @param array
     * @return {string}
     */
    decrypt(array): string {
        const block = array;
        AES_Decrypt(block, this.key);

        return this.bin2String(block);
    }

    /**
     * convert binary array to string
     *
     * @param array
     * @return {string}
     */
    bin2String(array): string {
        let result = '';
        for (const item of array) {
            result += String.fromCharCode(parseInt(item, 2));
        }

        return result;
    }

    /**
     * convert binary array to url
     *
     * @param input
     * @return {string}
     */
    bin2url(input): string {
        let output = '';
        let chr1;
        let chr2;
        let chr3;
        let enc1;
        let enc2;
        let enc3;
        let enc4;
        let i = 0;

        while (i < input.length) {
            chr1 = input[i++];
            chr2 = input[i++];
            chr3 = input[i++];
            enc1 = chr1 >> 2;
            enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
            enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
            enc4 = chr3 & 63;
            output += this.chars.charAt(enc1);
            output += this.chars.charAt(enc2);
            output += this.chars.charAt(enc3);
            output += this.chars.charAt(enc4);
        }

        return output;
    }

    /**
     * convert url to binary array
     *
     * @param input
     * @return {Array<any>}
     */
    url2bin(input): Array<any> {
        let output = [];
        let chr1;
        let chr2;
        let chr3;
        let enc1;
        let enc2;
        let enc3;
        let enc4;
        let i = 0;

        while (i < input.length) {
            enc1 = this.chars.indexOf(input.charAt(i++));
            enc2 = this.chars.indexOf(input.charAt(i++));
            enc3 = this.chars.indexOf(input.charAt(i++));
            enc4 = this.chars.indexOf(input.charAt(i++));
            chr1 = (enc1 << 2) | (enc2 >> 4);
            chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
            chr3 = ((enc3 & 3) << 6) | enc4;
            output = output.concat([chr1, chr2, chr3]);
        }

        return output;
    }

    /**
     * convert string to binary array
     *
     * @param {string} str
     * @return {Array<any>}
     */
    string2Bin(str: string): Array<any> {
        const result = [];
        for (let i = 0; i < str.length; i++) {
            result.push(str.charCodeAt(i));
        }

        return result;
    }

    /**
     * set crypt key
     *
     * @param {string} key
     */
    setKey(key: string): void {
        this._key = this.url2bin((key + this.primeKey).substr(0, 24)).slice(0, 16);
        // this.keyHash = this.utilityService.hash(key)
        AES_ExpandKey(this.key);
    }

    getSession(): string {
        // CookieService.set(...) wants expiresDate in days
        const oneHour = 1 / 24;
        let currentPrefix = '-1';

        currentPrefix = this.applicationStateService.bookConfig.quickLinkPrefix;
        let existingPrefix = '';

        if (this.cookieService.check(this.HSM_SESSION_PREFIX)) {
            existingPrefix = this.cookieService.get(this.HSM_SESSION_PREFIX);
        }

        const shouldRequestNewTimestamp = this.isRelatedPrefix(currentPrefix, existingPrefix);

        if (!this.cookieService.check(this.HSM_SESSION_TOKEN) || shouldRequestNewTimestamp) {
            const sessionToken = `${Date.now() / 1000}`;
            this.cookieService.set(this.HSM_SESSION_TOKEN, sessionToken, oneHour, '/', null, true);
            this.cookieService.set(this.HSM_SESSION_PREFIX, currentPrefix, oneHour, '/', null, true);
            this.cookieService.delete(this.TOKEN_KEY);

            return sessionToken;
        }

        return this.cookieService.get(this.HSM_SESSION_TOKEN);
    }

    isRelatedPrefix(currentPrefix: string, existingPrefix: string): boolean {
        return currentPrefix !== existingPrefix;
    }

    /**
     * encrypt nickname
     *
     * @param {string} newnick
     * @return {string}
     */
    encNick(newnick: string): string {
        let nickname = '';
        if (newnick.length) {
            nickname = newnick;
        } else {
            nickname = this.getSession();
            nickname = `Anonym ${nickname.substr(nickname.length - 5)}`;
        }

        let encNickname = nickname;
        if ((nickname.indexOf('Anonym') < 0) && (nickname.indexOf('"fa-') < 0)) {
            encNickname = this.encryptSaltedString(nickname);
        }
        // setCredential("nick", encNickname);

        return encNickname;
    }
}
